import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Grid3x3, ArrowRight, Lock, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import VelocityCard from "@/components/VelocityCard";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  committed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Calibration() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [velocity, setVelocity] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        api.get("/calibration/sessions"),
        api.get("/calibration/velocity").catch(() => ({ data: null })),
      ]);
      setItems(r.data.items || []);
      setVelocity(v.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="max-w-6xl space-y-6" data-testid="calibration-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">HR · Calibration</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Calibration sessions</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Run a 9-box session with HR + leaders, adjust ratings, and lock outcomes into performance history.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-session-btn"><Plus className="w-4 h-4 mr-2" /> New session</Button>
          </DialogTrigger>
          <CreateSessionDialog onClose={() => { setOpen(false); reload(); }} />
        </Dialog>
      </div>

      {velocity && velocity.total > 0 && (
        <VelocityCard
          velocity={velocity}
          overline="Calibration commits · last 8 weeks"
          unit="session locked"
          unitPlural="sessions locked"
          primaryColor="#a855f7"
          testId="calibration-velocity-card"
          totalsLabel={({ total }) => `8wk total: ${total} session${total === 1 ? "" : "s"} committed`}
          interpret={(v) => {
            if (v.total === 0) return "No sessions committed yet.";
            if (v.delta_wow > 0) return "Calibration meetings landing — ratings are getting locked in.";
            const last = v.weeks[v.weeks.length - 2];
            if (v.delta_wow < 0 && last && last.count > 0) return "Slower commit pace this week. Schedule the next meeting?";
            return "Steady commit cadence.";
          }}
        />
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading sessions…</div>
      ) : items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500" data-testid="empty-sessions">
          <Grid3x3 className="w-7 h-7 mx-auto text-slate-300" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">No calibration sessions yet</h2>
          <p className="text-sm mt-1 max-w-sm mx-auto">Create one for an active cycle to start placing employees on the 9-box.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s) => (
            <Link key={s.id} to={`/calibration/${s.id}`} className="axia-card p-5 hover:border-slate-400 transition" data-testid={`session-row-${s.id}`}>
              <div className="flex items-center gap-2">
                <span className={`axia-pill ${STATUS_STYLE[s.status]}`}>{s.status.replace("_", " ")}</span>
                <span className="axia-overline">{s.phase.replace("_", "-")}</span>
              </div>
              <div className="font-display text-lg font-semibold text-slate-900 mt-2">{s.name}</div>
              <div className="text-sm text-slate-500 mt-1">{s.cycle_name}</div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <span>{s.created_at ? format(parseISO(s.created_at), "d MMM yyyy") : ""}</span>
                <span className="inline-flex items-center gap-1 text-slate-500">Open <ArrowRight className="w-3 h-3" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateSessionDialog({ onClose }) {
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [phase, setPhase] = useState("year_end");
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cycles").then((r) => {
      const list = r.data.items || [];
      setCycles(list);
      if (list[0]) setCycleId(list[0].id);
    });
  }, []);

  const submit = async () => {
    if (!cycleId || !name) return toast.error("Cycle and name required");
    setSaving(true);
    try {
      const r = await api.post("/calibration/sessions", {
        cycle_id: cycleId,
        phase,
        name,
        scope_department: department || null,
      });
      toast.success(`Session created with ${r.data.participant_count ?? "—"} participants`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="create-session-dialog">
      <DialogHeader>
        <DialogTitle>New calibration session</DialogTitle>
        <DialogDescription>Auto-populates participants from existing reviews.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Cycle</Label>
          <Select value={cycleId} onValueChange={setCycleId}>
            <SelectTrigger data-testid="cycle-select"><SelectValue placeholder="Select cycle" /></SelectTrigger>
            <SelectContent>
              {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Phase</Label>
          <Select value={phase} onValueChange={setPhase}>
            <SelectTrigger data-testid="phase-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mid_year">Mid-year</SelectItem>
              <SelectItem value="year_end">Year-end</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Session name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. YE 2026 — Engineering" data-testid="name-input" />
        </div>
        <div>
          <Label>Limit to department (optional)</Label>
          <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Leave blank for all" data-testid="dept-input" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="create-submit-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Create session
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
